import { useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import CountUp from 'react-countup';
import { usePageTitle } from '../lib/usePageTitle';

import mareanaLogo from '../assets/company-logos/mareana-logo.png';
import farmwattLogo from '../assets/company-logos/farmwatt-full-logo.png';
import activeHippyLogo from '../assets/company-logos/activehippy-logo.svg';
import vuNetSystemsLogo from '../assets/company-logos/vunet-systems-logo.webp';
import valGenesisLogo from '../assets/company-logos/valgenesis-logo.svg';
import tpfSoftwareLogo from '../assets/company-logos/tpf-software-logo.svg';
import cloudNowLogo from '../assets/company-logos/cloudnow-logo.svg';
import toppanMerrillLogo from '../assets/company-logos/toppan-merrill-logo.svg';

const CLIENT_LOGOS = [
  { name: 'Mareana', logo: mareanaLogo },
  { name: 'Farmwatt', logo: farmwattLogo },
  { name: 'ActiveHippy', logo: activeHippyLogo },
  { name: 'VuNet Systems', logo: vuNetSystemsLogo },
  { name: 'ValGenesis', logo: valGenesisLogo },
  { name: 'TPF Software', logo: tpfSoftwareLogo },
  { name: 'CloudNow', logo: cloudNowLogo },
  { name: 'Toppan Merrill', logo: toppanMerrillLogo },
] as const;

const STATS = [
  { value: 4, unit: '', suffix: '', label: 'Countries served', allAccent: true },
  { value: 5, unit: 'M', suffix: '+', label: 'Profiles in our corpus', allAccent: false },
  { value: 100, unit: 'K', suffix: '+', label: 'Fresh CVs, every month', allAccent: false },
  { value: 10, unit: 'x', suffix: '', label: 'Recruiter throughput', allAccent: true },
] as const;

const VISION_POINTS = [
  {
    title: "We're an AI-powered recruiting platform",
    body: 'A matching engine that runs your search at 10x speed, with a dedicated recruiter and full accountability behind every shortlist — not a black box, and not a bigger recruiter roster.',
  },
  {
    title: 'Run on an AI engine we keep to ourselves',
    body: 'Internally, our recruiters work from a matching engine that pre-ranks our entire candidate pool before a human opens a req. You never see it — you see the outcome.',
  },
  {
    title: 'Built on a corpus that keeps compounding',
    body: "5M+ profiles, refreshed with 100K+ new CVs every month. Nothing we've ever sourced goes cold — it keeps getting re-matched against every new role.",
  },
  {
    title: 'Operating across four countries, one desk',
    body: 'The United States, Canada, India, and Thailand — the same speed and rigor, wherever the role sits.',
  },
] as const;

const COUNTRIES = [
  { name: 'United States', flag: '🇺🇸' },
  { name: 'Canada', flag: '🇨🇦' },
  { name: 'India', flag: '🇮🇳' },
  { name: 'Thailand', flag: '🇹🇭' },
] as const;

const VIDEO_SOURCES = [
  'https://assets.mixkit.co/videos/4547/4547-720.mp4',
  'https://assets.mixkit.co/videos/4809/4809-720.mp4',
  'https://assets.mixkit.co/active_storage/video_items/100310/1722986663/100310-video-720.mp4',
] as const;

export const HomePage = () => {
  const rootRef = useRef<HTMLElement>(null);
  usePageTitle('QuantumHire — Recruitment, run at the speed of intelligence');

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const elements = root.querySelectorAll<HTMLElement>('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15 },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
    <main ref={rootRef} className="qh-home">
      <HeroSection />

      <section className="logo-strip" aria-label="Trusted companies">
        <div className="wrap">
          <p className="logo-strip-label mono">
            Trusted by 200+ companies across the US, Canada, India &amp; Thailand
          </p>
        </div>
        <div className="logo-track-wrap">
          <div className="logo-track">
            {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((logo, index) => (
              <span
                className="logo-item"
                key={`${logo.name}-${index}`}
                aria-hidden={index >= CLIENT_LOGOS.length ? true : undefined}
              >
                <img
                  className={
                    logo.name === 'Mareana'
                      ? 'logo-image logo-image--mareana'
                      : 'logo-image'
                  }
                  src={logo.logo}
                  alt={logo.name}
                />
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="split">
        <div className="split-inner">
          <Link to="/employers" className="split-card employer reveal">
            <div>
              <span className="split-tag mono">// For companies hiring</span>
              <h2>I&apos;m an employer</h2>
              <p>
                Send us a role. Our platform returns a ranked shortlist faster than a traditional
                agency search — a recruiter reviews every match, and you own the final call.
              </p>
            </div>
            <span className="split-cta">
              See how we work <span className="arrow">→</span>
            </span>
          </Link>

          <Link to="/job-seekers" className="split-card jobseeker reveal">
            <div>
              <span className="split-tag mono">// For people looking for work</span>
              <h2>I&apos;m a jobseeker</h2>
              <p>
                Browse live roles across four countries, or submit your profile once and let our
                matching engine flag you the moment a real fit opens up.
              </p>
            </div>
            <span className="split-cta">
              Find your next role <span className="arrow">→</span>
            </span>
          </Link>
        </div>
      </section>

      <section className="stats">
        <div className="stat-grid">
          {STATS.map((stat) => (
            <div className={`stat-item reveal${stat.allAccent ? ' all-accent' : ''}`} key={stat.label}>
              <div className="num">
                <span className="count">
                  <CountUp
                    end={stat.value}
                    duration={2.25}
                    enableScrollSpy
                    scrollSpyOnce
                  />
                </span>
                {stat.unit && <span className="unit">{stat.unit}</span>}
                {stat.suffix && <span className="accent">{stat.suffix}</span>}
              </div>
              <div className="label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="what" id="what">
        <div className="what-grid">
          <div>
            <span className="tag">// Our vision</span>
            <h2>We think hiring shouldn&apos;t move at the speed of a calendar.</h2>
          </div>
          <div className="what-points">
            {VISION_POINTS.map((point, index) => (
              <article className="what-point reveal" key={point.title}>
                <span className="idx mono">{String(index + 1).padStart(2, '0')}</span>
                <h3>{point.title}</h3>
                <p>{point.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="countries" id="countries">
        <span className="tag mono">// Where we work</span>
        <h2>Four countries. One AI-augmented desk.</h2>
        <div className="country-row">
          {COUNTRIES.map((country) => (
            <div className="country-item reveal" key={country.name}>
              <div className="name">{country.name}</div>
              <div className="flag" aria-hidden="true">{country.flag}</div>
            </div>
          ))}
        </div>
      </section>

      <HomeStyles />
    </main>
  );
};

const HeroSection = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videosRef = useRef<Array<HTMLVideoElement | null>>([]);

  useLayoutEffect(() => {
    videosRef.current.forEach((video) => {
      if (!video) return;
      video.muted = true;
      video.setAttribute('muted', '');
    });
  }, []);

  useEffect(() => {
    const videos = videosRef.current.filter((video): video is HTMLVideoElement => Boolean(video));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      videos.forEach((video, index) => {
        video.pause();
        video.classList.toggle('active', index === 0);
      });
      return;
    }

    let activeIndex = 0;
    const interval = window.setInterval(() => {
      videos[activeIndex]?.classList.remove('active');
      activeIndex = (activeIndex + 1) % videos.length;
      videos[activeIndex]?.classList.add('active');
    }, 7000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ratio = window.devicePixelRatio || 1;
    let width = 0;
    let height = 0;
    let animationFrame = 0;

    type NodePoint = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      pulse: number;
      hot: boolean;
    };

    const nodes: NodePoint[] = [];

    const resize = () => {
      width = canvas.width = canvas.offsetWidth * ratio;
      height = canvas.height = canvas.offsetHeight * ratio;
    };

    resize();
    const nodeCount = Math.min(70, Math.floor((canvas.offsetWidth * canvas.offsetHeight) / 16000));
    for (let index = 0; index < nodeCount; index += 1) {
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25 * ratio,
        vy: (Math.random() - 0.5) * 0.25 * ratio,
        radius: (Math.random() * 1.6 + 0.8) * ratio,
        pulse: Math.random() * Math.PI * 2,
        hot: Math.random() < 0.12,
      });
    }

    const step = () => {
      context.clearRect(0, 0, width, height);

      for (let firstIndex = 0; firstIndex < nodes.length; firstIndex += 1) {
        for (let secondIndex = firstIndex + 1; secondIndex < nodes.length; secondIndex += 1) {
          const first = nodes[firstIndex];
          const second = nodes[secondIndex];
          const deltaX = first.x - second.x;
          const deltaY = first.y - second.y;
          const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
          const maxDistance = 150 * ratio;

          if (distance < maxDistance) {
            const alpha = (1 - distance / maxDistance) * 0.14;
            context.strokeStyle = `rgba(28,170,60,${alpha})`;
            context.lineWidth = 1;
            context.beginPath();
            context.moveTo(first.x, first.y);
            context.lineTo(second.x, second.y);
            context.stroke();
          }
        }
      }

      nodes.forEach((node) => {
        if (!reduceMotion) {
          node.x += node.vx;
          node.y += node.vy;
          if (node.x < 0 || node.x > width) node.vx *= -1;
          if (node.y < 0 || node.y > height) node.vy *= -1;
          node.pulse += 0.02;
        }

        const glow = node.hot ? (Math.sin(node.pulse) + 1) / 2 : 0.15;
        context.beginPath();
        context.arc(
          node.x,
          node.y,
          node.radius + (node.hot ? glow * 2 * ratio : 0),
          0,
          Math.PI * 2,
        );
        context.fillStyle = node.hot
          ? `rgba(200,255,77,${0.55 + glow * 0.45})`
          : 'rgba(245,246,242,0.32)';
        context.fill();
      });

      if (!reduceMotion) animationFrame = window.requestAnimationFrame(step);
    };

    step();
    window.addEventListener('resize', resize);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <header className="hero">
      <div className="hero-video-bg" aria-hidden="true">
        {VIDEO_SOURCES.map((source, index) => (
          <video
            className={index === 0 ? 'active' : ''}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            src={source}
            ref={(video) => {
              videosRef.current[index] = video;
            }}
            key={source}
          />
        ))}
      </div>
      <div className="hero-video-overlay" aria-hidden="true" />
      <canvas ref={canvasRef} aria-hidden="true" />
      <div className="hero-glow a" aria-hidden="true" />
      <div className="hero-glow b" aria-hidden="true" />

      <div className="hero-inner">
        <div className="eyebrow mono">
          <span className="dot-live" /> FTE Hiring that combines Human Judgement with AI Speed
        </div>
        <h1>
          Hiring decisions,
          <br />
          made at the <em>speed of intelligence.</em>
        </h1>
        <p className="lede">
          QuantumHire is an AI-powered recruiting platform that finds, ranks, and delivers
          candidates at 10x the speed of a manual search — with real recruiters reviewing every
          shortlist. You just see who gets hired, or who gets hired next.
        </p>
        <div className="hero-badge-row">
          <span className="hero-badge">4 countries</span>
          <span className="hero-badge">5M+ profiles</span>
          <span className="hero-badge">10x recruiter speed</span>
        </div>
      </div>

      <a href="#what" className="scroll-cue">
        <span>Scroll</span>
        <span className="chevron" />
      </a>
    </header>
  );
};

const HomeStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

    .qh-home {
      --ink:#14150F;
      --ink-soft:#3A3C35;
      --paper:#F5F6F2;
      --paper-dim:#EBECE5;
      --quantum:#1CAA3C;
      --quantum-deep:#0B5C22;
      --quantum-tint:#E7F5EA;
      --signal:#C8FF4D;
      --steel:#6B6F68;
      --line:rgba(20,21,15,0.10);
      --line-dark:rgba(245,246,242,0.14);
      width:100%;
      max-width:none;
      margin:0;
      padding:0;
      border:0;
      border-radius:0;
      overflow-x:hidden;
      background:var(--paper);
      color:var(--ink);
      font-family:'Inter',sans-serif;
      line-height:normal;
      -webkit-font-smoothing:antialiased;
    }
    .qh-home * { box-sizing:border-box; }
    .qh-home .mono { font-family:'JetBrains Mono',monospace; letter-spacing:0.02em; }
    .qh-home h1,.qh-home h2,.qh-home h3 {
      font-family:'Poppins',sans-serif;
      font-weight:600;
      letter-spacing:-0.02em;
    }
    .qh-home a { color:inherit; text-decoration:none; }
    .qh-home button { font:inherit; cursor:pointer; }
    .qh-home :focus-visible { outline:2px solid var(--quantum); outline-offset:3px; }
    .qh-home .wrap { max-width:1180px; margin:0 auto; padding:0 32px; }

    .qh-home .reveal { opacity:0; transform:translateY(24px); transition:opacity .7s ease,transform .7s ease; }
    .qh-home .reveal.in-view { opacity:1; transform:translateY(0); }
    .qh-home .reveal:nth-child(2) { transition-delay:.08s; }
    .qh-home .reveal:nth-child(3) { transition-delay:.16s; }
    .qh-home .reveal:nth-child(4) { transition-delay:.24s; }
    .qh-home .reveal:nth-child(5) { transition-delay:.32s; }

    .qh-home .hero {
      width:100%;
      max-width:none;
      margin:0;
      border-radius:0;
      padding:110px 32px 70px;
      border-bottom:1px solid var(--line);
      position:relative;
      overflow:hidden;
      min-height:58vh;
      display:flex;
      align-items:center;
      justify-content:center;
      background:var(--ink);
      color:var(--paper);
    }
    .qh-home .hero-video-bg { position:absolute; inset:0; z-index:0; overflow:hidden; background:var(--ink); }
    .qh-home .hero-video-bg video {
      position:absolute;
      inset:0;
      width:100%;
      height:100%;
      object-fit:cover;
      opacity:0;
      transition:opacity 1.6s ease;
    }
    .qh-home .hero-video-bg video.active { opacity:1; }
    .qh-home .hero-video-overlay {
      position:absolute;
      inset:0;
      z-index:1;
      background:linear-gradient(180deg,rgba(20,21,15,.74) 0%,rgba(20,21,15,.56) 45%,rgba(20,21,15,.82) 100%);
    }
    .qh-home .hero canvas { position:absolute; inset:0; width:100%; height:100%; opacity:.4; z-index:3; }
    .qh-home .hero-glow { position:absolute; border-radius:50%; filter:blur(80px); pointer-events:none; z-index:2; }
    .qh-home .hero-glow.a { width:480px; height:480px; background:var(--quantum); opacity:.35; top:-160px; left:-100px; }
    .qh-home .hero-glow.b { width:420px; height:420px; background:var(--quantum); opacity:.22; bottom:-180px; right:-80px; }
    .qh-home .hero-inner { position:relative; z-index:4; max-width:820px; margin:0 auto; text-align:center; }
    .qh-home .hero-inner > * { opacity:0; transform:translateY(18px); animation:homeHeroIn .8s ease forwards; }
    .qh-home .hero-inner .eyebrow { animation-delay:.05s; }
    .qh-home .hero-inner h1 { animation-delay:.18s; }
    .qh-home .hero-inner p.lede { animation-delay:.34s; }
    .qh-home .eyebrow {
      display:inline-flex;
      align-items:center;
      gap:10px;
      font-size:11.5px;
      text-transform:uppercase;
      color:var(--signal);
      border:1px solid var(--line-dark);
      padding:7px 12px;
      border-radius:2px;
      margin-bottom:24px;
      background:rgba(245,246,242,.06);
    }
    .qh-home .dot-live {
      width:6px;
      height:6px;
      border-radius:50%;
      background:var(--quantum);
      box-shadow:0 0 6px var(--quantum);
      animation:homePulse 2s infinite;
    }
    .qh-home .hero h1 { font-size:clamp(32px,5.6vw,60px); line-height:1.06; margin:0 0 18px; }
    .qh-home .hero h1 em {
      font-style:normal;
      background:linear-gradient(100deg,var(--signal),var(--quantum) 70%);
      -webkit-background-clip:text;
      background-clip:text;
      color:transparent;
    }
    .qh-home .hero p.lede { font-size:16.5px; color:rgba(245,246,242,.72); line-height:1.6; max-width:580px; margin:0 auto; }
    .qh-home .hero-badge-row { display:flex; align-items:center; justify-content:center; gap:12px; flex-wrap:wrap; margin-top:28px; }
    .qh-home .hero-badge {
      font-family:'JetBrains Mono',monospace;
      font-size:12.5px;
      font-weight:500;
      background:rgba(245,246,242,.08);
      border:1px solid var(--line-dark);
      border-radius:20px;
      padding:8px 16px;
      backdrop-filter:blur(4px);
      color:var(--paper);
      opacity:0;
      transform:translateY(12px);
      animation:homeHeroIn .6s ease forwards;
    }
    .qh-home .hero-badge:nth-child(1) { animation-delay:.5s; }
    .qh-home .hero-badge:nth-child(2) { animation-delay:.66s; }
    .qh-home .hero-badge:nth-child(3) { animation-delay:.82s; }
    .qh-home .scroll-cue {
      position:absolute;
      bottom:20px;
      left:50%;
      transform:translateX(-50%);
      z-index:4;
      display:flex;
      flex-direction:column;
      align-items:center;
      gap:6px;
      font-family:'JetBrains Mono',monospace;
      font-size:11px;
      color:rgba(245,246,242,.5);
      text-transform:uppercase;
      letter-spacing:.05em;
    }
    .qh-home .scroll-cue .chevron {
      width:14px;
      height:14px;
      border-right:2px solid rgba(245,246,242,.5);
      border-bottom:2px solid rgba(245,246,242,.5);
      transform:rotate(45deg);
      animation:scrollBounce 1.8s infinite;
    }

    .qh-home .logo-strip { padding:48px 0 60px; border-bottom:1px solid var(--line); background:var(--paper); }
    .qh-home .logo-strip-label { text-align:center; font-size:12px; color:var(--steel); text-transform:uppercase; letter-spacing:.04em; margin:0 0 30px; }
    .qh-home .logo-track-wrap {
      overflow:hidden;
      padding-bottom:40px;
      margin-bottom:-40px;
      -webkit-mask-image:linear-gradient(to right,transparent,black 8%,black 92%,transparent);
      mask-image:linear-gradient(to right,transparent,black 8%,black 92%,transparent);
    }
    .qh-home .logo-track { display:flex; align-items:center; gap:56px; width:max-content; animation:logoScroll 30s linear infinite; }
    .qh-home .logo-item {
      position:relative;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      height:40px;
      transition:transform .2s;
    }
    .qh-home .logo-item .logo-image {
      display:block;
      width:auto;
      max-width:160px;
      height:100%;
      object-fit:contain;
    }
    .qh-home .logo-item .logo-image--mareana { height:40px; }
    .qh-home .logo-item:hover { transform:translateY(-2px); }

    .qh-home .split { padding:0 32px 100px; }
    .qh-home .split-inner { max-width:1180px; margin:0 auto; display:grid; grid-template-columns:1fr 1fr; gap:1px; background:var(--line); border:1px solid var(--line); border-radius:8px; overflow:hidden; }
    .qh-home .split-card {
      position:relative;
      padding:64px 48px;
      background:var(--paper);
      display:flex;
      flex-direction:column;
      justify-content:space-between;
      min-height:340px;
      transition:background .2s,transform .2s,box-shadow .2s;
      cursor:pointer;
      text-align:left;
      border:0;
      border-radius:0;
      color:inherit;
    }
    .qh-home .split-card.employer { background:var(--ink); color:var(--paper); }
    .qh-home .split-card:hover { background:var(--quantum-tint); transform:translateY(-3px); box-shadow:0 16px 32px rgba(20,21,15,.10); }
    .qh-home .split-card.employer:hover { background:#1b2416; }
    .qh-home .split-tag { font-size:11.5px; text-transform:uppercase; letter-spacing:.04em; opacity:.6; margin-bottom:20px; display:block; }
    .qh-home .split-card h2 { font-size:clamp(24px,2.6vw,32px); line-height:1.15; margin:0 0 14px; }
    .qh-home .split-card p { font-size:15px; line-height:1.6; opacity:.75; max-width:380px; margin:0; }
    .qh-home .split-cta { margin-top:40px; display:inline-flex; align-items:center; gap:10px; font-weight:600; font-size:15px; }
    .qh-home .split-card.jobseeker .split-cta { color:var(--quantum-deep); }
    .qh-home .split-card.employer .split-cta { color:var(--quantum); }
    .qh-home .split-cta .arrow { transition:transform .15s; }
    .qh-home .split-card:hover .split-cta .arrow { transform:translateX(4px); }

    .qh-home .stats { background:var(--paper-dim); padding:64px 32px; border-top:1px solid var(--line); border-bottom:1px solid var(--line); }
    .qh-home .stat-grid { max-width:1180px; margin:0 auto; display:grid; grid-template-columns:repeat(4,1fr); gap:0; }
    .qh-home .stat-item { text-align:center; padding:0 20px; border-left:1px solid var(--line); }
    .qh-home .stat-item:first-child { border-left:none; }
    .qh-home .stat-item .num { font-family:'Poppins',sans-serif; font-size:38px; font-weight:600; color:var(--ink); }
    .qh-home .stat-item .num .count,.qh-home .stat-item .num .count span,.qh-home .stat-item .num .unit { color:var(--ink); }
    .qh-home .stat-item .num .accent { color:var(--quantum-deep); }
    .qh-home .stat-item.all-accent .num,.qh-home .stat-item.all-accent .num span { color:var(--quantum-deep); }
    .qh-home .stat-item .label { font-family:'JetBrains Mono',monospace; font-size:11px; text-transform:uppercase; color:var(--steel); margin-top:8px; letter-spacing:.03em; }

    .qh-home section.what { padding:100px 32px; }
    .qh-home .what-grid { max-width:1180px; margin:0 auto; display:grid; grid-template-columns:.9fr 1.1fr; gap:64px; align-items:start; }
    .qh-home .tag { font-family:'JetBrains Mono',monospace; font-size:11.5px; text-transform:uppercase; color:var(--quantum-deep); letter-spacing:.04em; margin-bottom:16px; display:block; }
    .qh-home .what-grid h2 { font-size:clamp(26px,3vw,36px); line-height:1.15; margin:0; }
    .qh-home .what-points { display:flex; flex-direction:column; gap:0; }
    .qh-home .what-point { padding:26px 0; border-top:1px solid var(--line); }
    .qh-home .what-point:first-child { border-top:none; }
    .qh-home .what-point .idx { font-size:12px; color:var(--quantum-deep); margin-bottom:8px; display:block; }
    .qh-home .what-point h3 { font-size:18px; margin:0 0 8px; }
    .qh-home .what-point p { font-size:14.5px; color:var(--steel); line-height:1.6; margin:0; }

    .qh-home .countries { background:var(--ink); color:var(--paper); padding:70px 32px; text-align:center; }
    .qh-home .countries .tag { color:var(--quantum); opacity:.9; }
    .qh-home .countries h2 { font-size:clamp(24px,3vw,32px); margin:0 0 36px; }
    .qh-home .country-row { max-width:900px; margin:0 auto; display:flex; justify-content:center; gap:0; flex-wrap:wrap; }
    .qh-home .country-item { flex:1; min-width:180px; padding:20px 16px; border-left:1px solid rgba(245,246,242,.14); }
    .qh-home .country-item:first-child { border-left:none; }
    .qh-home .country-item .name { font-family:'Poppins',sans-serif; font-weight:600; font-size:17px; margin-bottom:10px; }
    .qh-home .country-item .flag { font-size:34px; line-height:1; }

    @keyframes homeHeroIn { to { opacity:1; transform:translateY(0); } }
    @keyframes homePulse { 0%,100% { opacity:1; } 50% { opacity:.35; } }
    @keyframes scrollBounce { 0%,100% { transform:rotate(45deg) translate(0,0); } 50% { transform:rotate(45deg) translate(4px,4px); } }
    @keyframes logoScroll { from { transform:translateX(0); } to { transform:translateX(calc(-50% - 28px)); } }

    @media (max-width:860px) {
      .qh-home .split-inner { grid-template-columns:1fr; }
      .qh-home .split-card { padding:44px 30px; min-height:auto; }
      .qh-home .what-grid { grid-template-columns:1fr; gap:32px; }
    }
    @media (max-width:700px) {
      .qh-home .stat-grid { grid-template-columns:repeat(2,1fr); row-gap:32px; }
      .qh-home .stat-item:nth-child(2n) { border-left:1px solid var(--line); }
      .qh-home .stat-item:nth-child(1),.qh-home .stat-item:nth-child(2) { border-left:none; }
      .qh-home .country-item { border-left:none; border-top:1px solid rgba(245,246,242,.14); min-width:45%; }
      .qh-home .country-item:first-child { border-top:none; }
    }
    @media (max-width:640px) {
      .qh-home .hero { min-height:auto; padding:100px 24px 60px; }
      .qh-home .hero-video-bg { display:none; }
    }
    @media (prefers-reduced-motion:reduce) {
      .qh-home .reveal { opacity:1; transform:none; transition:none; }
      .qh-home .hero-inner > *,.qh-home .hero-badge { opacity:1; transform:none; animation:none; }
      .qh-home .scroll-cue .chevron,.qh-home .logo-track,.qh-home .dot-live { animation:none; }
    }
  `}</style>
);
